// Maps the report strategy endpoint's `data` (GET / POST /intel-reports/{reportId}/strategy, see
// strategiesApi.js) onto the strategy UI's shape. This is the only place that knows the endpoint's
// field names, so a contract change stays a one-file fix. Fields the page doesn't read aren't
// carried: add them here when a consumer needs them.
//
// data:   { ready, pendingInputs[],
//           run: { id, week, status: queued|running|completed|failed|cancelled,
//                  trigger: initial|refresh, createdAt, completedAt|null, errorMessage|null } | null,
//           strategy: (the latest COMPLETED run's content, below) | null }
//         `ready` / `pendingInputs` are kept by the slice (byReport), not on the row.
// UI row: { id (strategy.reportId, else run.id: the run the content came from; the campaign wizard
//             passes it as strategyId), intelReportRequestId, name, status (STRATEGY_STATUS),
//           error (run.errorMessage),
//           completedAt (strategy.generatedAt: the CONTENT's date, so a refresh in flight keeps
//             the previous week's "Last updated on"),
//           body (normalizeStrategyBody(strategy), null until the first run completes) }
//
// strategy → body:
//   objective                               → same
//   playbookNeeds[]                         → same (string[]; the notice shows while needs remain)
//   competitorCount                         → competitive.competitorCount
//   overview { headline, summary } | null   → overview (null stays null); counts are the SHOWN
//                                             sections' sizes so the chips match the sections, not
//                                             the BE's `counts`
//   audiences[] (by rank)                   → audiences (priorityShare 0–1 → priorityScore 0–100,
//                                             tagline is the descriptor, imageUrl → image)
//   trends[] (by opportunity.score desc)    → trends (sparkline of momentum.series[].vectorPct,
//                                             the last SPARKLINE_POINTS months)
//   topics[] (BE order)                     → competitive.topics (stance → posture; a 'defend'
//                                             topic is one the brand leads)
//   plays[] (by rank)                       → plays (the BE's trend / topic `meta` lines as-is,
//                                             emoji for the card's tile)

import dayjs from '../../../utils/dayjs';
import { TREND_STATUS_META } from '../../CulturalIntelligence/utils/culturalIntelligenceTokens';
import {
  STRATEGY_STATUS,
  SPARKLINE_POINTS,
  momentumWordOf,
  momentumWordOfDirection,
  scoreTierOf,
  strategyTitleOf,
} from '../static/constants';

const API_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const IN_FLIGHT = ['queued', 'running'];
const FAILED = ['failed', 'cancelled'];

const arrayOf = (value) => (Array.isArray(value) ? value : []);

const toNumber = (value) => {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const toIsoDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value, API_DATE_FORMAT, true);
  const date = parsed.isValid() ? parsed : dayjs(value);
  return date.isValid() ? date.toISOString() : null;
};

const lower = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const strings = (value) => arrayOf(value).filter((item) => typeof item === 'string' && item);

/**
 * The latest run's `{ status, trigger, week }` → STRATEGY_STATUS. A run in flight is UPDATING when
 * it's a refresh (trigger 'refresh', or any week after the first): the previous week stays readable.
 * No run, or an unknown status, reads as NOT_GENERATED.
 */
const normalizeStrategyStatus = (run) => {
  const status = lower(run?.status);
  if (IN_FLIGHT.includes(status)) {
    const refresh = lower(run?.trigger) === 'refresh' || (toNumber(run?.week) ?? 0) > 0;
    return refresh ? STRATEGY_STATUS.UPDATING : STRATEGY_STATUS.GENERATING;
  }
  if (status === 'completed') return STRATEGY_STATUS.READY;
  if (FAILED.includes(status)) return STRATEGY_STATUS.FAILED;
  return STRATEGY_STATUS.NOT_GENERATED;
};

const byRank = (a, b) => (toNumber(a?.rank) ?? Infinity) - (toNumber(b?.rank) ?? Infinity);

// One `audiences[]` entry → a "Who to reach" card.
const normalizeAudience = (audience = {}) => {
  const share = toNumber(audience.priorityShare);
  return {
    id: audience.segmentId ?? null,
    name: audience.name || null,
    descriptor: audience.tagline || null,
    // Presigned S3 link, valid 60 minutes; null means the card's placeholder.
    image: audience.imageUrl || null,
    priorityScore: share == null ? null : Math.round(share * 100),
  };
};

/**
 * One `trends[]` entry → the "Trends to ride" row:
 *   { id, title, culture,
 *     type,             // TrendTypePill key, or null (no pill)
 *     momentum,         // the "+32%" number: momentum.vectorPct, rounded
 *     momentumWord,     // MOMENTUM_WORD from momentum.direction ('collapsing' → falling)
 *     series,           // [{ month, value: vectorPct }], oldest first, the last SPARKLINE_POINTS
 *     opportunityScore, scoreTier }
 */
const normalizeTrend = (trend = {}) => {
  const momentum = trend.momentum || {};
  const vectorPct = toNumber(momentum.vectorPct);
  const opportunityScore = toNumber(trend.opportunity?.score);
  const type = lower(trend.type);
  return {
    id: trend.id ?? null,
    title: trend.title || null,
    culture: trend.culture || null,
    type: TREND_STATUS_META[type] ? type : null,
    // Whole percent: formatMomentum doesn't round.
    momentum: vectorPct == null ? null : Math.round(vectorPct),
    momentumWord: momentumWordOfDirection(momentum.direction) ?? momentumWordOf(vectorPct),
    series: arrayOf(momentum.series)
      .map((point) => ({ month: point?.month || null, value: toNumber(point?.vectorPct) }))
      .slice(-SPARKLINE_POINTS),
    opportunityScore,
    scoreTier: scoreTierOf(opportunityScore),
  };
};

const byOpportunity = (a, b) =>
  (b.opportunityScore ?? -Infinity) - (a.opportunityScore ?? -Infinity);

const shareRowOf = (row) =>
  row ? { product: row.product || null, share: toNumber(row.share) } : null;

// One `topics[]` entry → a "Where you stand" card; anything but 'defend' is attacked. Per the
// contract a defended topic is one the brand leads, so its bar compares against `second`.
const normalizeTopic = (topic = {}) => {
  const shareOfVoice = topic.shareOfVoice || {};
  const youShare = toNumber(shareOfVoice.client);
  return {
    topic: topic.topic || null,
    posture: lower(topic.stance) === 'defend' ? 'defend' : 'attack',
    insight: topic.insight || null,
    description: topic.description || null,
    you: youShare == null ? null : { share: youShare },
    leader: shareRowOf(shareOfVoice.leader),
    second: shareRowOf(shareOfVoice.second),
  };
};

// One `plays[]` entry → a "Possible plays" card. The BE preformats the trend / topic meta lines.
const normalizePlay = (play = {}) => ({
  rank: toNumber(play.rank),
  emoji: play.emoji || null,
  title: play.title || null,
  summary: play.summary || null,
  channelTypes: strings(play.channelTypes),
  segments: arrayOf(play.segments).map((segment) => ({
    name: segment?.name || null,
    // "58% female · 15–44"
    demographics: segment?.demographics || null,
  })),
  trend: play.trend ? { title: play.trend.title || null, meta: play.trend.meta || null } : null,
  topic: play.topic ? { topic: play.topic.topic || null, meta: play.topic.meta || null } : null,
});

/**
 * The endpoint's `strategy` (the latest completed run's content) → the strategy page's body; null
 * without one.
 * @param {object|null} strategy
 * @returns {object|null}
 */
function normalizeStrategyBody(strategy) {
  if (!strategy) return null;
  const audiences = arrayOf(strategy.audiences)
    .filter(Boolean)
    .sort(byRank)
    .map(normalizeAudience);
  const trends = arrayOf(strategy.trends)
    .filter(Boolean)
    .map(normalizeTrend)
    .sort(byOpportunity);
  const topics = arrayOf(strategy.topics)
    .filter(Boolean)
    .map(normalizeTopic);
  const plays = arrayOf(strategy.plays)
    .filter(Boolean)
    .sort(byRank)
    .map(normalizePlay);

  return {
    objective: strategy.objective || null,
    playbookNeeds: strings(strategy.playbookNeeds),
    overview: strategy.overview
      ? {
          headline: strategy.overview.headline || null,
          summary: strategy.overview.summary || null,
          // What the page shows, so the chips match the sections below them.
          counts: {
            segments: audiences.length,
            trends: trends.length,
            topics: topics.length,
            plays: plays.length,
          },
        }
      : null,
    audiences,
    trends,
    competitive: { competitorCount: toNumber(strategy.competitorCount), topics },
    plays,
  };
}

/**
 * @param {number|string} reportId - The intel report the strategy belongs to.
 * @param {object} data - The GET / POST response's `data` ({ ready, pendingInputs, run, strategy }).
 * @param {{ report?: object }} [options] - `report`: the normalized intel-report row (IR slice,
 *   `{ name, objective }`), for the title.
 * @returns {object} The strategy's UI row.
 */
export default function normalizeStrategy(reportId, data = {}, { report } = {}) {
  const run = data.run || null;
  const strategy = data.strategy || null;

  return {
    id: strategy?.reportId ?? run?.id ?? null,
    intelReportRequestId: reportId,
    name: strategyTitleOf(
      strategy?.objective ?? report?.objective ?? null,
      strategy?.product ?? report?.name ?? null
    ),
    status: normalizeStrategyStatus(run),
    error: run?.errorMessage || null,
    completedAt: toIsoDate(strategy?.generatedAt),
    body: normalizeStrategyBody(strategy),
  };
}
